import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useCart, cartItemHref } from '../../hooks/useCart'
import {
  TrashIcon,
  ShoppingCartIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline'

const CartPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { items, subtotal, vendorName, removeItem, setQuantity, itemCount } = useCart()

  if (!items.length) {
    return (
      <div className="max-w-3xl mx-auto text-center py-16">
        <ShoppingCartIcon className="h-16 w-16 mx-auto text-gray-300" />
        <h2 className="mt-4 text-xl font-semibold text-gray-900">
          {t('cart.empty')}
        </h2>
        <p className="mt-2 text-gray-500">{t('cart.emptyHint')}</p>
        <Link
          to="/products"
          className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          {t('cart.continueShopping')}
        </Link>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {t('cart.title')} ({itemCount})
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Items */}
        <div className="lg:col-span-2 space-y-4">
          <p className="text-sm text-gray-500">
            {t('cart.soldBy')}: <span className="font-medium text-gray-700">{vendorName}</span>
          </p>
          {items.map((item) => (
            <div
              key={item.productId}
              className="bg-white shadow rounded-lg p-4 flex items-center gap-4"
            >
              <Link to={cartItemHref(item)} className="shrink-0">
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.name}
                    className="h-20 w-20 object-cover rounded-lg"
                  />
                ) : (
                  <div className="h-20 w-20 bg-gray-200 rounded-lg" />
                )}
              </Link>

              <div className="flex-1 min-w-0">
                <Link
                  to={cartItemHref(item)}
                  className="text-sm font-medium text-gray-900 hover:text-primary-600 line-clamp-2"
                >
                  {item.name}
                </Link>
                <p className="mt-1 text-sm text-gray-500">
                  RM{item.price.toFixed(2)}
                </p>
              </div>

              <div className="flex items-center border border-gray-300 rounded-lg">
                <button
                  onClick={() => setQuantity(item.productId, item.quantity - 1)}
                  disabled={item.quantity <= 1}
                  className="px-3 py-1 text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                >
                  -
                </button>
                <span className="w-10 text-center text-sm">{item.quantity}</span>
                <button
                  onClick={() => setQuantity(item.productId, item.quantity + 1)}
                  className="px-3 py-1 text-gray-600 hover:bg-gray-100"
                >
                  +
                </button>
              </div>

              <div className="text-right w-24">
                <p className="text-sm font-semibold text-gray-900">
                  RM{(item.price * item.quantity).toFixed(2)}
                </p>
                <button
                  onClick={() => removeItem(item.productId)}
                  className="mt-1 inline-flex items-center text-xs text-red-600 hover:text-red-800"
                >
                  <TrashIcon className="h-4 w-4 mr-1" />
                  {t('cart.remove')}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="bg-white shadow rounded-lg p-6 h-fit">
          <h3 className="text-lg font-medium text-gray-900">{t('cart.summary')}</h3>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">{t('cart.subtotal')}</span>
              <span className="font-medium">RM{subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t('checkout.shippingFee')}</span>
              <span className="text-gray-500">{t('cart.calculatedAtCheckout')}</span>
            </div>
          </div>
          <button
            onClick={() => navigate('/checkout')}
            className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
          >
            {t('cart.checkout')}
            <ArrowRightIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default CartPage
